import { useFirebase } from 'containers/FirebaseContext';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import type { Reminder, ReminderResponse, ReminderStatus } from '../types/reminder';

const REMINDERS_COLLECTION = 'reminders';
const REMINDER_RESPONSES_COLLECTION = 'reminderResponses';

function reminderToFirestore(reminder: Omit<Reminder, 'reminderId' | 'createdAt' | 'updatedAt'>) {
  return {
    classId: reminder.classId,
    teacherId: reminder.teacherId,
    message: reminder.message,
    reminderAt: reminder.reminderAt,
    finalReminderTime: reminder.finalReminderTime,
    softReminderTime: reminder.softReminderTime,
    selectedStudents: reminder.selectedStudents,
    targetAllStudents: reminder.targetAllStudents,
    status: reminder.status,
  };
}

function toMillis(val: unknown): number {
  if (val == null) return 0;
  if (typeof val === 'number') return val;
  const t = val as { toMillis?: () => number };
  return t.toMillis ? t.toMillis() : 0;
}

function responseActionTimeToMillis(val: unknown): number | null {
  if (val == null) return null;
  if (typeof val === 'number' && !Number.isNaN(val)) return val;
  const t = val as { toMillis?: () => number };
  if (typeof t.toMillis === 'function') return t.toMillis();
  return null;
}

function normalizeResponseStatus(status: unknown): ReminderStatus {
  if (status === 'final_reminder_shown') return 'pending';
  return (status as ReminderStatus) ?? 'pending';
}

function firestoreToReminder(id: string, data: Record<string, unknown>): Reminder {
  return {
    reminderId: id,
    classId: (data.classId as string) ?? '',
    teacherId: (data.teacherId as string) ?? '',
    message: (data.message as string) ?? '',
    reminderAt: (data.reminderAt as string) ?? null,
    finalReminderTime: (toMillis(data.finalReminderTime) || (data.finalReminderTime as number)) ?? 0,
    softReminderTime: data.softReminderTime != null ? (toMillis(data.softReminderTime) || (data.softReminderTime as number)) : null,
    selectedStudents: Array.isArray(data.selectedStudents) ? (data.selectedStudents as string[]) : [],
    targetAllStudents: (data.targetAllStudents as boolean) ?? false,
    createdAt: (toMillis(data.createdAt) || (data.createdAt as number)) ?? 0,
    updatedAt: (toMillis(data.updatedAt) || (data.updatedAt as number)) ?? 0,
    status: (data.status as 'active' | 'cancelled') ?? 'active',
  };
}

export function useReminderFirebase() {
  const { firestore } = useFirebase();

  const subscribeRemindersByClass = (
    classId: string,
    onUpdate: (reminders: Reminder[]) => void
  ) => {
    if (!classId || !firestore) return () => {};
    const q = query(
      collection(firestore, REMINDERS_COLLECTION),
      where('classId', '==', classId)
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const reminders: Reminder[] = [];
        snapshot.forEach((d) => {
          const data = d.data();
          reminders.push(firestoreToReminder(d.id, { ...data }));
        });
        onUpdate(reminders);
      },
      (err) => {
        if (err.code === 'permission-denied') return;
        console.error('reminders subscribe error', err);
      }
    );
    return unsubscribe;
  };

  const subscribeRemindersForStudent = (
    classId: string,
    studentId: string,
    onUpdate: (reminders: Reminder[]) => void
  ) => {
    if (!classId || !studentId || !firestore) return () => {};
    const coll = collection(firestore, REMINDERS_COLLECTION);
    const qAll = query(
      coll,
      where('classId', '==', classId),
      where('status', '==', 'active'),
      where('targetAllStudents', '==', true)
    );
    const qSpecific = query(
      coll,
      where('classId', '==', classId),
      where('status', '==', 'active'),
      where('selectedStudents', 'array-contains', studentId)
    );
    let allList: Reminder[] = [];
    let specificList: Reminder[] = [];
    const toReminderList = (snap: { forEach: (fn: (d: { id: string; data: () => Record<string, unknown> }) => void) => void }): Reminder[] => {
      const list: Reminder[] = [];
      snap.forEach((d) => {
        const data = d.data();
        list.push(firestoreToReminder(d.id, { ...data }));
      });
      return list;
    };
    const merge = () => {
      const byId = new Map<string, Reminder>();
      allList.forEach((r) => byId.set(r.reminderId, r));
      specificList.forEach((r) => byId.set(r.reminderId, r));
      const list = Array.from(byId.values());
      onUpdate(list);
    };
    const unsubAll = onSnapshot(
      qAll,
      (snap) => {
        allList = toReminderList(snap as unknown as { forEach: (fn: (d: { id: string; data: () => Record<string, unknown> }) => void) => void });
        merge();
      },
      (err) => {
        if (err.code === 'permission-denied') return;
        console.error('reminders for student (all) subscribe error', err);
      }
    );
    const unsubSpecific = onSnapshot(
      qSpecific,
      (snap) => {
        specificList = toReminderList(snap as unknown as { forEach: (fn: (d: { id: string; data: () => Record<string, unknown> }) => void) => void });
        merge();
      },
      (err) => {
        if (err.code === 'permission-denied') return;
        console.error('reminders for student (specific) subscribe error', err);
      }
    );
    return () => {
      unsubAll();
      unsubSpecific();
    };
  };

  const createReminder = async (
    payload: Omit<Reminder, 'reminderId' | 'updatedAt'>
  ): Promise<string | null> => {
    if (!firestore) return null;
    const ref = collection(firestore, REMINDERS_COLLECTION);
    const { createdAt, ...rest } = payload;
    const updatedAt = Date.now();
    const docRef = await addDoc(ref, {
      ...reminderToFirestore(rest),
      createdAt,
      updatedAt,
    });
    return docRef.id;
  };

  const updateReminder = async (
    reminderId: string,
    payload: Partial<Pick<Reminder, 'message' | 'finalReminderTime' | 'softReminderTime' | 'selectedStudents' | 'targetAllStudents' | 'status'>>
  ) => {
    if (!firestore) return;
    const ref = doc(firestore, REMINDERS_COLLECTION, reminderId);
    await updateDoc(ref, {
      ...payload,
      updatedAt: Date.now(),
    });
  };

  const deleteReminder = async (reminderId: string) => {
    if (!firestore) return;
    const ref = doc(firestore, REMINDERS_COLLECTION, reminderId);
    await updateDoc(ref, { status: 'cancelled', updatedAt: Date.now() });
  };

  const subscribeResponsesForStudent = (
    studentId: string,
    onUpdate: (responses: Record<string, ReminderResponse>) => void
  ) => {
    if (!studentId || !firestore) return () => {};
    const q = query(
      collection(firestore, REMINDER_RESPONSES_COLLECTION),
      where('studentId', '==', studentId)
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const out: Record<string, ReminderResponse> = {};
        snapshot.forEach((d) => {
          const data = d.data();
          const rid = data.reminderId as string;
          if (rid) {
            out[rid] = {
              status: normalizeResponseStatus(data.status),
              snoozeCount: (data.snoozeCount as number) ?? 0,
              actionTime: responseActionTimeToMillis(data.actionTime),
            };
          }
        });
        onUpdate(out);
      },
      (err) => {
        if (err.code === 'permission-denied') return;
        console.error('reminderResponses for student subscribe error', err);
      }
    );
    return unsubscribe;
  };

  const subscribeResponsesForReminder = (
    reminderId: string,
    onUpdate: (responses: Record<string, ReminderResponse>) => void
  ) => {
    if (!reminderId || !firestore) return () => {};
    const q = query(
      collection(firestore, REMINDER_RESPONSES_COLLECTION),
      where('reminderId', '==', reminderId)
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const out: Record<string, ReminderResponse> = {};
        snapshot.forEach((d) => {
          const data = d.data();
          const studentId = data.studentId as string;
          if (studentId) {
            out[studentId] = {
              status: normalizeResponseStatus(data.status),
              snoozeCount: (data.snoozeCount as number) ?? 0,
              actionTime: responseActionTimeToMillis(data.actionTime),
            };
          }
        });
        onUpdate(out);
      },
      (err) => {
        if (err.code === 'permission-denied') return;
        console.error('reminderResponses subscribe error', err);
      }
    );
    return unsubscribe;
  };

  const setReminderResponse = async (
    reminderId: string,
    studentId: string,
    response: Partial<ReminderResponse>
  ) => {
    if (!firestore) return;
    const docId = `${reminderId}_${studentId}`;
    const ref = doc(firestore, REMINDER_RESPONSES_COLLECTION, docId);
    const existing = await getDoc(ref);
    const now = Date.now();
    const shouldSetActionTime =
      response.status === 'snoozed' || response.status === 'dismissed';

    if (existing.exists()) {
      const data = existing.data();
      const payload: Record<string, unknown> = {
        ...response,
        snoozeCount: response.snoozeCount ?? (data?.snoozeCount ?? 0),
      };
      if (shouldSetActionTime) {
        payload.actionTime = now;
      }
      await updateDoc(ref, payload);
    } else {
      await setDoc(ref, {
        reminderId,
        studentId,
        status: response.status ?? 'pending',
        snoozeCount: response.snoozeCount ?? 0,
        actionTime: shouldSetActionTime ? now : null,
      });
    }
  };

  return {
    subscribeRemindersByClass,
    subscribeRemindersForStudent,
    createReminder,
    updateReminder,
    deleteReminder,
    subscribeResponsesForReminder,
    subscribeResponsesForStudent,
    setReminderResponse,
  };
}
